/**
 * Chat Message Component
 *
 * Renders a single message in the chat panel.
 * Supports user messages, assistant messages (with markdown code blocks),
 * and tool execution status indicators interleaved with text.
 */

import React from 'react';
import * as styles from './chat-styles';
import { speakMessage, subscribeTts, getTtsStatus, type TtsState } from './tts-playback';

export interface ToolCall {
    name: string;
    status: 'running' | 'done' | 'error';
    detail?: string;
    objectPath?: string;
    model?: string;
    /**
     * Live status while the tool runs — 3D generation and the inference
     * services take minutes, and a chip that only says "running" for that
     * long reads as a hang. Cleared when the tool settles.
     */
    progress?: string;
}

/** A segment is either a text chunk or a tool call, rendered in order */
export type MessageSegment =
    | { type: 'text'; text: string }
    | { type: 'tool'; tool: ToolCall };

export interface MessageData {
    id: string;
    role: 'user' | 'assistant';
    content: string;
    segments?: MessageSegment[];
    thinking?: string;
    /** User messages: names of the files sent with it. */
    attachments?: string[];
    /** Assistant messages: the turn paused and can be continued. */
    paused?: boolean;
}

// ── Simple markdown rendering for code blocks ───────────────

function renderContent(text: string): React.ReactNode[] {
    const parts: React.ReactNode[] = [];
    const codeBlockRegex = /```(\w*)\n?([\s\S]*?)```/g;
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = codeBlockRegex.exec(text)) !== null) {
        // Text before code block
        if (match.index > lastIndex) {
            parts.push(
                <span key={`t${lastIndex}`}>
                    {renderInlineText(text.slice(lastIndex, match.index))}
                </span>
            );
        }

        // Code block
        parts.push(
            <pre key={`c${match.index}`} style={styles.codeBlock}>
                {match[2]}
            </pre>
        );

        lastIndex = match.index + match[0].length;
    }

    // Remaining text after last code block
    if (lastIndex < text.length) {
        parts.push(
            <span key={`t${lastIndex}`}>
                {renderInlineText(text.slice(lastIndex))}
            </span>
        );
    }

    return parts;
}

function renderInlineText(text: string): React.ReactNode[] {
    // Split on double newlines into paragraphs, then handle inline code and single newlines
    const parts: React.ReactNode[] = [];
    const paragraphs = text.split(/\n{2,}/);

    paragraphs.forEach((para, pi) => {
        if (pi > 0) {
            parts.push(<br key={`br2-${pi}a`} />);
            parts.push(<br key={`br2-${pi}b`} />);
        }
        // Split paragraph on single newlines
        const lines = para.split('\n');
        lines.forEach((line, li) => {
            if (li > 0) {
                parts.push(<br key={`br-${pi}-${li}`} />);
            }
            // Handle inline code within each line
            const inlineCodeRegex = /`([^`]+)`/g;
            let lastIndex = 0;
            let match: RegExpExecArray | null;
            while ((match = inlineCodeRegex.exec(line)) !== null) {
                if (match.index > lastIndex) {
                    parts.push(line.slice(lastIndex, match.index));
                }
                parts.push(
                    <code key={`ic${pi}-${li}-${match.index}`} style={{
                        background: '#1a1a1a',
                        padding: '1px 4px',
                        borderRadius: 3,
                        fontSize: 12,
                        fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                    }}>
                        {match[1]}
                    </code>
                );
                lastIndex = match.index + match[0].length;
            }
            if (lastIndex < line.length) {
                parts.push(line.slice(lastIndex));
            }
        });
    });

    return parts;
}

// ── Tool Status Indicator ───────────────────────────────────

const TOOL_LABELS: Record<string, string> = {
    rate_limit: 'Waiting for rate limit',
    create_script: 'Creating script',
    edit_script: 'Editing script',
    get_scene_structure: 'Reading scene',
    get_scene_tree: 'Reading scene tree',
    wire_group_state: 'Wiring group state',
    wire_interaction: 'Wiring interaction',
    save_scene: 'Saving scene',
    get_script_source: 'Reading script',
    create_object: 'Creating',
    set_transform: 'Setting transform',
    select_object: 'Selecting',
    focus_camera: 'Moving camera',
    add_behavior: 'Adding behavior',
    get_behavior: 'Reading behavior',
    commit_behavior: 'Updating behavior',
    attach_script: 'Attaching script',
    list_behaviors: 'Looking up behaviors',
    set_color: 'Setting color',
    search_catalog: 'Searching assets',
    web_search: 'Searching the web',
    fetch_url: 'Reading',
    import_asset: 'Importing asset',
    build_spatial: 'Building',
    generate_image: 'Generating image',
    generate_splat: 'Generating splat',
    generate_model: 'Generating 3D model',
    generate_3d: 'Converting to 3D',
};

/** Safely coerce an unknown value to string (for display in template literals). */
const s = (v: unknown): string => (typeof v === 'string' ? v : '');

/** A call over several objects: its list (`objects` or `entries`), or null for a call on one. */
const listOf = (input: Record<string, unknown>): unknown[] | null =>
    Array.isArray(input.objects) ? input.objects : Array.isArray(input.entries) ? input.entries : null;
/** The object a list entry names: a name, or an entry's `object`. */
const entryRef = (entry: unknown): string | undefined =>
    typeof entry === 'string' ? entry : entry && typeof entry === 'object' ? (entry as { object?: unknown }).object as string | undefined : undefined;
/** "3 objects" for a call over a list, else what `one` says about the single object. */
const oneOrMany = (input: Record<string, unknown>, one: string): string => {
    const list = listOf(input);
    return list ? `${list.length} ${list.length === 1 ? 'object' : 'objects'}` : one;
};

/** Extract a human-readable detail string from tool input */
export function getToolDetail(toolName: string, input: Record<string, unknown>): string {
    switch (toolName) {
        case 'create_script':
            return `${s(input.name)}${input.object ? ` on ${s(input.object)}` : ''}`;
        case 'edit_script':
            return s(input.name);
        case 'get_script_source':
            return s(input.name);
        case 'create_object':
            return input.name ? `${s(input.template)} "${s(input.name)}"` : s(input.template);
        case 'set_transform':
        case 'set_active':
            return oneOrMany(input, s(input.object));
        case 'select_object':
            return s(input.object);
        case 'add_behavior':
            return oneOrMany(input, `${s(input.behavior_type)} on ${s(input.object)}`);
        case 'get_behavior':
            return `${s(input.behavior_type)} on ${s(input.object)}`;
        case 'commit_behavior':
            return oneOrMany(input, `${s(input.behavior_type)} on ${s(input.object)}`);
        case 'attach_script':
            return `${s(input.script_name)} to ${s(input.object)}`;
        case 'list_behaviors':
            return s(input.behavior_type);
        case 'set_color':
            return oneOrMany(input, `${s(input.object)} ${s(input.color)}`);
        case 'web_search':
            return s(input.query);
        case 'fetch_url':
            return input.pages ? `${s(input.url)} (pages ${s(input.pages)})` : s(input.url);
        case 'search_catalog':
            return s(input.query);
        case 'import_asset':
            return s(input.name);
        case 'build_spatial':
            return s(input.description);
        case 'generate_image':
            return s(input.prompt).slice(0, 60);
        case 'generate_splat':
            return input.object ? s(input.object) : s(input.prompt).slice(0, 60);
        case 'generate_model':
            return s(input.prompt).slice(0, 60);
        default:
            return '';
    }
}

/** Extract the object path/name from tool input for click-to-select */
export function getToolObjectRef(toolName: string, input: Record<string, unknown>): string | undefined {
    switch (toolName) {
        case 'create_script':
            return input.object as string | undefined;
        case 'create_object':
            return undefined; // object doesn't exist yet
        case 'set_color':
        case 'set_transform':
        case 'set_active':
        case 'add_behavior':
        case 'commit_behavior':
            // A call over a list selects its first object.
            return input.object as string | undefined ?? entryRef(listOf(input)?.[0]);
        case 'select_object':
        case 'get_behavior':
            return input.object as string | undefined;
        case 'attach_script':
            return input.object as string | undefined;
        default:
            return undefined;
    }
}

function ToolStatusIndicator({ tool, onClickObject }: {
    tool: ToolCall;
    onClickObject?: (objectPath: string) => void;
}) {
    const label = TOOL_LABELS[tool.name] || tool.name;
    const detail = tool.detail;
    const isClickable = !!tool.objectPath && !!onClickObject;

    const statusIndicator = tool.status === 'running' ? (
        <span style={{
            display: 'inline-block',
            width: 6,
            height: 6,
            flexShrink: 0,
            marginTop: 5,
            borderRadius: '50%',
            background: styles.colors.accent,
            animation: 'ai-pulse 1.2s ease-in-out infinite',
        }} />
    ) : (
        <span style={{
            color: tool.status === 'done' ? styles.colors.success : styles.colors.error,
        }}>
            {tool.status === 'done' ? '\u2713' : '\u2717'}
        </span>
    );

    return (
        <div
            style={{
                ...styles.toolStatus,
                cursor: isClickable ? 'pointer' : 'default',
            }}
            onClick={isClickable ? () => onClickObject(tool.objectPath!) : undefined}
            title={isClickable ? `Select "${tool.objectPath}"` : undefined}
        >
            {statusIndicator}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                <span>{label}{detail ? ` ${detail}` : ''}</span>
                {tool.status === 'running' && tool.progress
                    ? <span style={{ opacity: 0.7, fontSize: 10 }}>{tool.progress}</span>
                    : tool.model && <span style={{ opacity: 0.5, fontSize: 10 }}>{tool.model}</span>}
            </div>
        </div>
    );
}

// ── Thinking Section ────────────────────────────────────────

function ThinkingSection({ text }: { text: string }) {
    const [expanded, setExpanded] = React.useState(false);
    // Show a one-line summary when collapsed
    const summary = text.split('\n')[0].slice(0, 80);
    return (
        <div style={{
            alignSelf: 'flex-start',
            fontSize: 12,
            color: styles.colors.textMuted,
            maxWidth: '85%',
        }}>
            <button
                onClick={() => setExpanded(!expanded)}
                style={{
                    background: 'none',
                    border: 'none',
                    color: styles.colors.textMuted,
                    cursor: 'pointer',
                    padding: '2px 0',
                    fontSize: 12,
                    fontFamily: 'inherit',
                    textAlign: 'left',
                }}
            >
                {expanded ? '\u25BE' : '\u25B8'} Thinking: {expanded ? '' : summary + (text.length > 80 ? '...' : '')}
            </button>
            {expanded && (
                <div style={{
                    marginTop: 4,
                    padding: '6px 10px',
                    background: '#1a1a1a',
                    borderRadius: 4,
                    whiteSpace: 'pre-wrap',
                    lineHeight: 1.4,
                    maxHeight: 200,
                    overflowY: 'auto',
                }}>
                    {text}
                </div>
            )}
        </div>
    );
}

// ── Speaker Button ──────────────────────────────────────────

/** Extract the spoken text from a message — concatenated text segments, falling back to content. */
function getSpokenText(msg: MessageData): string {
    if (msg.segments && msg.segments.length > 0) {
        return msg.segments
            .filter((s): s is { type: 'text'; text: string } => s.type === 'text')
            .map(s => s.text)
            .join(' ')
            .trim();
    }
    return msg.content.trim();
}

function useTtsStatusFor(id: string): TtsState | 'inactive' {
    const [status, setStatus] = React.useState(getTtsStatus);
    React.useEffect(() => subscribeTts(setStatus), []);
    return status.activeId === id ? status.state : 'inactive';
}

function SpeakButton({ messageId, text }: { messageId: string; text: string }) {
    const state = useTtsStatusFor(messageId);
    const active = state !== 'inactive';

    const title = state === 'loading' ? 'Loading...'
        : state === 'playing' ? 'Stop'
            : 'Play';

    return (
        <button
            onClick={() => void speakMessage(messageId, text)}
            title={title}
            style={{
                alignSelf: 'flex-start',
                marginTop: 2,
                background: 'transparent',
                border: 'none',
                padding: '2px 4px',
                cursor: 'pointer',
                color: active ? styles.colors.accent : styles.colors.textMuted,
                opacity: active ? 1 : 0.6,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                fontSize: 11,
                lineHeight: 1,
            }}
        >
            {state === 'playing' ? (
                <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
                    <rect x="4" y="4" width="3" height="8" rx="0.5" />
                    <rect x="9" y="4" width="3" height="8" rx="0.5" />
                </svg>
            ) : state === 'loading' ? (
                <span style={{
                    display: 'inline-block',
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: styles.colors.accent,
                    animation: 'ai-pulse 1.2s ease-in-out infinite',
                }} />
            ) : (
                <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M8 2.5 4.5 5.5H2v5h2.5L8 13.5v-11z" />
                    <path d="M10.5 5.5a3 3 0 0 1 0 5" stroke="currentColor" strokeWidth="1" fill="none" strokeLinecap="round" />
                    <path d="M12 4a5 5 0 0 1 0 8" stroke="currentColor" strokeWidth="1" fill="none" strokeLinecap="round" />
                </svg>
            )}
        </button>
    );
}

// ── Main Component ──────────────────────────────────────────

export const ChatMessageComponent: React.FC<{
    message: MessageData;
    onClickObject?: (objectPath: string) => void;
    /** Shown as a Continue button; the panel passes it only while the turn can resume. */
    onContinue?: () => void;
}> = ({ message, onClickObject, onContinue }) => {
    // If we have segments, render them interleaved; otherwise fall back to old layout
    const segments = message.segments;
    const hasSegments = segments && segments.length > 0;
    const spokenText = message.role === 'assistant' ? getSpokenText(message) : '';

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {/* Thinking section (collapsible) */}
            {message.thinking && <ThinkingSection text={message.thinking} />}

            {hasSegments ? (
                // Interleaved segments: text bubbles and tool indicators in order
                segments.map((seg, i) => {
                    if (seg.type === 'tool') {
                        return <ToolStatusIndicator key={`seg-${i}`} tool={seg.tool} onClickObject={onClickObject} />;
                    }
                    // Text segment
                    if (!seg.text) return null;
                    return (
                        <div key={`seg-${i}`} style={message.role === 'user' ? styles.userMessage : styles.assistantMessage}>
                            {message.role === 'assistant'
                                ? renderContent(seg.text)
                                : seg.text}
                        </div>
                    );
                })
            ) : (
                // Legacy: single content string (for user messages)
                <>
                    {message.content && (
                        <div style={message.role === 'user' ? styles.userMessage : styles.assistantMessage}>
                            {message.role === 'assistant'
                                ? renderContent(message.content)
                                : message.content}
                        </div>
                    )}
                </>
            )}
            {message.attachments && message.attachments.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'flex-end', marginTop: 4 }}>
                    {message.attachments.map((name, i) => (
                        <span key={i} style={styles.attachmentChip} title={name}>{name}</span>
                    ))}
                </div>
            )}
            {onContinue && (
                <button
                    onClick={onContinue}
                    title="Run the same turn for another round of tool iterations"
                    style={{ ...styles.smallButton, alignSelf: 'flex-start', padding: '3px 10px', fontSize: 11 }}
                >
                    Continue
                </button>
            )}
            {message.role === 'assistant' && spokenText && (
                <SpeakButton messageId={message.id} text={spokenText} />
            )}
        </div>
    );
};
